import { useCallback, useMemo } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { faClone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReportTemplateEntityType, type ReportTemplateTypeFieldsFragment } from '@/graphql/types';
import { ConfirmType, useConfirm } from '../../confirm/Confirm';
import { RoundedButton } from '../../form/Button';
import { BasePage } from '../BasePage';
import {
    REPORT_TEMPLATE_PAGE_ORIENTATION_TRANSLATIONS,
    REPORT_TEMPLATE_PAGE_SIZE_TRANSLATIONS,
    translateEntityType,
    translateReportTemplateUsage,
} from './enums';
import { pageMarginToCss } from './pageSettings';
import { useProfile } from '../../../profile';
import getDeleteMessage from '../../../utils/messages';
import { format } from '../../../utils/formatting';
import {
    useReportTemplatesQueryGenerator,
    createEditUrl,
    useCreateReportTemplateMutation,
    useDeleteReportTemplateMutation,
    useDuplicateReportTemplateMutation,
    type ReportTemplateInUse,
} from './utils';
import { useOrderAndPaginationControl } from '../../table/hooks';
import Table from '../../table/Table';
import type { TableProps } from '../../table/Table';
import LoadingSpinner from '../../loadingSpinner/LoadingSpinner';
import Pagination from '../../table/Pagination';
import { canEditReportTemplate } from './reportTemplatePermissions';

type ReportTemplateColumns = TableProps<ReportTemplateTypeFieldsFragment>['columns'];
export const REPORT_TEMPLATES_TABLE_SORT = {
    tableId: 'report-templates',
    sortableFields: ['name'],
} as const;

function formatPageSettings(template: ReportTemplateTypeFieldsFragment): string {
    return [
        REPORT_TEMPLATE_PAGE_SIZE_TRANSLATIONS[template.pageSize],
        REPORT_TEMPLATE_PAGE_ORIENTATION_TRANSLATIONS[template.pageOrientation],
        pageMarginToCss(template.pageMargin),
    ].join(' / ');
}

function getTemplateInUseMessage({
    usages,
    remainingCount,
    hiddenCount,
    canForce,
}: ReportTemplateInUse): JSX.Element {
    return (
        <div className="space-y-3 text-left leading-5">
            <p>{gettext('REPORT_TEMPLATE_IN_USE')}</p>
            <ol className="ml-5 list-decimal space-y-1">
                {usages.map((usage, index) => (
                    <li key={index}>{translateReportTemplateUsage(usage)}</li>
                ))}
            </ol>
            {remainingCount > 0 && (
                <p>
                    {interpolate(
                        gettext('REPORT_TEMPLATE_IN_USE_MORE'),
                        { count: remainingCount },
                        true
                    )}
                </p>
            )}
            {hiddenCount > 0 && (
                <p>
                    {interpolate(
                        ngettext(
                            'REPORT_TEMPLATE_IN_USE_HIDDEN_SINGULAR',
                            'REPORT_TEMPLATE_IN_USE_HIDDEN_PLURAL',
                            hiddenCount
                        ),
                        { count: hiddenCount },
                        true
                    )}
                </p>
            )}
            <p>
                {canForce
                    ? gettext('REPORT_TEMPLATE_DELETE_RESET')
                    : gettext('REPORT_TEMPLATE_DELETE_BLOCKED')}
            </p>
        </div>
    );
}

export function OverviewPage(): JSX.Element {
    const confirm = useConfirm();
    const { canAccessReportBuilderProjectTemplates } = useProfile();
    const { showBoundary } = useErrorBoundary();
    const createReportTemplateMutation = useCreateReportTemplateMutation();
    const deleteReportTemplateMutation = useDeleteReportTemplateMutation();
    const { duplicate: duplicateReportTemplate, duplicating } =
        useDuplicateReportTemplateMutation();

    const orderAndPaginationControl = useOrderAndPaginationControl(REPORT_TEMPLATES_TABLE_SORT);
    const { reportTemplates, loading, updating } =
        useReportTemplatesQueryGenerator(orderAndPaginationControl);

    const createTemplate = useCallback(
        async (entityType: ReportTemplateEntityType): Promise<void> => {
            try {
                await createReportTemplateMutation(entityType);
            } catch (error) {
                showBoundary(error);
            }
        },
        [createReportTemplateMutation, showBoundary]
    );

    const deleteConfirmedTemplate = useCallback(
        async function deleteOrShowUses(
            template: ReportTemplateTypeFieldsFragment,
            force = false
        ): Promise<void> {
            try {
                const inUse = await deleteReportTemplateMutation(template.id, force);
                if (!inUse) {
                    return;
                }

                if (!inUse.canForce) {
                    confirm({
                        type: ConfirmType.INFO,
                        message: getTemplateInUseMessage(inUse),
                    });
                    return;
                }

                confirm({
                    message: getTemplateInUseMessage(inUse),
                    confirmLabel: gettext('REPORT_TEMPLATE_DELETE_ANYWAY'),
                    onConfirm: () => {
                        // The server checks ownership and uses again.
                        void deleteOrShowUses(template, true);
                    },
                });
            } catch (error) {
                showBoundary(error);
            }
        },
        [confirm, deleteReportTemplateMutation, showBoundary]
    );

    const duplicateTemplate = useCallback(
        async (template: ReportTemplateTypeFieldsFragment): Promise<void> => {
            try {
                await duplicateReportTemplate(template.id);
            } catch (error) {
                showBoundary(error);
            }
        },
        [duplicateReportTemplate, showBoundary]
    );

    const deleteTemplate = useCallback(
        (template: ReportTemplateTypeFieldsFragment): void => {
            confirm({
                markdownMessage: getDeleteMessage(gettext('TEMPLATE'), template.name),
                onConfirm: () => {
                    void deleteConfirmedTemplate(template);
                },
            });
        },
        [confirm, deleteConfirmedTemplate]
    );

    const columns = useMemo<ReportTemplateColumns>(() => {
        const entityTypeColumns: ReportTemplateColumns = canAccessReportBuilderProjectTemplates
            ? [
                  {
                      key: 'entityType',
                      title: gettext('ENTITY_TYPE'),
                      render(_column, row) {
                          return translateEntityType(row.entityType);
                      },
                  },
              ]
            : [];

        return [
            {
                key: 'name',
                title: gettext('NAME'),
                render(_column, row) {
                    return (
                        <span className="inline-flex items-center gap-2">
                            <span>{row.name}</span>
                            {row.isSystem && (
                                <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-700">
                                    {gettext('SYSTEM_TEMPLATE_BADGE')}
                                </span>
                            )}
                        </span>
                    );
                },
            },
            ...entityTypeColumns,
            {
                key: 'pageSettings',
                title: `${gettext('PAGE_SIZE')} / ${gettext('ORIENTATION')} / ${gettext('MARGIN')}`,
                render(_column, row) {
                    return formatPageSettings(row);
                },
            },
            {
                key: 'modifiedAt',
                title: gettext('UPDATED_AT'),
                render(_column, row) {
                    return format(new Date(row.modifiedAt), {
                        useBrowserTimezone: true,
                    });
                },
            },
            {
                key: 'actions',
                title: (
                    <span className="space-x-2">
                        {updating && <LoadingSpinner className="h-5 align-middle text-gray-400" />}
                        {orderAndPaginationControl.showPagination && (
                            <Pagination orderAndPaginationControl={orderAndPaginationControl} />
                        )}
                    </span>
                ),
                render(_column, row) {
                    const canEdit = canEditReportTemplate(row.permission);
                    const duplicateLabel = `${gettext('DUPLICATE')} ${row.name}`;

                    return (
                        <div className="flex w-full justify-end gap-x-2">
                            {canEdit && (
                                <RoundedButton to={createEditUrl(row.id)}>
                                    {gettext('VIEW')}
                                </RoundedButton>
                            )}
                            <RoundedButton
                                variant="icon"
                                disabled={duplicating}
                                aria-label={duplicateLabel}
                                title={duplicateLabel}
                                onClick={() => void duplicateTemplate(row)}
                            >
                                <FontAwesomeIcon icon={faClone} />
                            </RoundedButton>
                            {canEdit && (
                                <RoundedButton
                                    color="secondary"
                                    onClick={() => deleteTemplate(row)}
                                >
                                    {gettext('DELETE')}
                                </RoundedButton>
                            )}
                        </div>
                    );
                },
            },
        ];
    }, [
        canAccessReportBuilderProjectTemplates,
        deleteTemplate,
        duplicateTemplate,
        duplicating,
        updating,
        orderAndPaginationControl,
    ]);

    return (
        <BasePage
            title={gettext('REPORT_TEMPLATE_OVERVIEW')}
            besideTitle={
                <div className="flex gap-x-3">
                    {canAccessReportBuilderProjectTemplates && (
                        <RoundedButton
                            onClick={() => void createTemplate(ReportTemplateEntityType.Project)}
                        >
                            {gettext('CREATE_PROJECT_TEMPLATE')}
                        </RoundedButton>
                    )}
                    <RoundedButton
                        onClick={() => void createTemplate(ReportTemplateEntityType.MeasuringPoint)}
                    >
                        {gettext('CREATE_MEASURING_POINT_TEMPLATE')}
                    </RoundedButton>
                </div>
            }
        >
            <Table
                data={reportTemplates}
                columns={columns}
                loading={loading || updating}
                orderAndPaginationControl={orderAndPaginationControl}
            />
        </BasePage>
    );
}
